import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ChatPage } from "./pages/Chat/ChatPage";
import { CartePublicPage } from "./pages/CartePublicPage";
import { PublicCartePage } from "./pages/PublicCartePage";
import { CartePage } from "./pages/Carte/CartePage";
import { LoginPage } from "./pages/Auth/Login/LoginPage";
import { SignupPage } from "./pages/Auth/Signup/SignupPage";
import { DashboardPage } from "./pages/Dashboard/DashboardPage";
import { ProfilePage } from "./pages/Profile/ProfilePage";
import { CustomersPage } from "./pages/Customers/CustomersPage";
import { OrdersPage } from "./pages/Orders/OrdersPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import type { Wine } from "./hooks/useWines";
import type { Brand } from "./pages/Carte/carte.types";

export const ROUTES = {
    VINAI: "/vinai",
    CARTE: "/carte",
    CARTE_PUBLIC: "/carte/public",
    CARTE_SHARED: "/carte/shared",
    CARTE_PUBLIC_HASH: "/carte/:publicCarteHash",
    DASHBOARD: "/dashboard",
    LOGIN: "/login",
    SIGNUP: "/signup",
    PROFILE: "/profile",
    CUSTOMERS: "/customers",
    ORDERS: "/orders",
} as const;

interface AppRoutesProps {
    lang: string;
    wines: Wine[];
    setWines: React.Dispatch<React.SetStateAction<Wine[]>>;
    maison: string;
    setMaison: (v: string) => void;
    updated: string;
    setUpdated: (v: string) => void;
    brand: Brand;
    setBrand: React.Dispatch<React.SetStateAction<Brand>>;
    adminEmail: string;
    setAdminEmail: React.Dispatch<React.SetStateAction<string>>;
    onPublic: () => void;
    onBack: () => void;
}

function RootRoute() {
    const location = useLocation();
    const search = new URLSearchParams(location.search);

    if (search.has("customerHash")) {
        return <PublicCartePage />;
    }

    return <Navigate to={ROUTES.VINAI} replace />;
}

export function AppRoutes({
    lang,
    wines,
    setWines,
    maison,
    setMaison,
    updated,
    setUpdated,
    brand,
    setBrand,
    adminEmail,
    setAdminEmail,
    onPublic,
    onBack,
}: AppRoutesProps) {
    return (
        <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
            <Route
                path={ROUTES.VINAI}
                element={
                    <ProtectedRoute>
                        <ChatPage maison={maison} lang={lang} />
                    </ProtectedRoute>
                }
            />
            <Route
                path={ROUTES.CARTE}
                element={
                    <ProtectedRoute>
                        <CartePage
                            wines={wines}
                            setWines={setWines}
                            maison={maison}
                            setMaison={setMaison}
                            updated={updated}
                            setUpdated={setUpdated}
                            brand={brand}
                            setBrand={setBrand}
                            onPublic={onPublic}
                            adminEmail={adminEmail}
                            setAdminEmail={setAdminEmail}
                        />
                    </ProtectedRoute>
                }
            />
            <Route
                path={ROUTES.CARTE_PUBLIC}
                element={
                    <CartePublicPage
                        wines={wines}
                        maison={maison}
                        updated={updated}
                        brand={brand}
                        onBack={onBack}
                    />
                }
            />
            <Route path={ROUTES.CARTE_SHARED} element={<PublicCartePage />} />
            <Route path={ROUTES.CARTE_PUBLIC_HASH} element={<PublicCartePage />} />
            <Route path={ROUTES.DASHBOARD} element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path={ROUTES.PROFILE} element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path={ROUTES.CUSTOMERS} element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
            <Route path={ROUTES.ORDERS} element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to={ROUTES.VINAI} replace />} />
        </Routes>
    );
}
